import axios from 'axios';
import * as Sentry from '@sentry/browser';
import qs from 'query-string';
import fetchCode from '@/utils/fetchCode';
import env from './env';

axios.defaults.timeout = 15000;
axios.defaults.withCredentials = true;

// 401请求池
const fetchPoll = {
  list: [],
  addPoll(option) {
    if (option.url !== '/refresh_token') {
      fetchPoll.list.push(option);
    }
  },
  delPoll(option) {
    fetchPoll.list = fetchPoll.list.filter(item => item.url !== option.url);
  }
};

// 请求拦截器
axios.interceptors.request.use(
  config => {
    const temp = config;
    if (temp.responseType === 'blob') temp.timeout = 1200000;
    if (temp.config && temp.config.timeout) temp.timeout = temp.config.timeout;
    return temp;
  },
  error => Promise.reject(error)
);

// 返回拦截器
axios.interceptors.response.use(
  response => {
    if (response.data.error_code && response.data.error_code !== fetchCode.SUCCESS) {
      window.$store.dispatch('msg/showErrorMsg', response.data.msg || '未知错误');
      return Promise.reject(response);
    }
    return response;
  },
  error => {
    const { response } = error;
    if (response) {
      switch (response.status) {
        case 400:
          window.$store.dispatch('msg/showErrorMsg', '请求异常！');
          return Promise.reject(response);
        case 401:
          // 返回 401 重新自动获取token信息
          if (response.data) {
            if (response.data.error_code === fetchCode.TOKEN_NEED_REFRESH) return Promise.resolve(response);
            if (response.config.responseType === 'blob' && response.data.type === 'application/json') {
              return Promise.resolve(response);
            }
          }
          return Promise.reject(response);
        case 404:
          window.$store.dispatch('msg/showErrorMsg', response.data.msg || response.statusText || 'Not Found！');
          return Promise.reject(response);
        case 500:
          return Promise.reject(response);
        default:
          return Promise.reject(response);
      }
    }
    Sentry.captureException(error);
    if (error.code === 'ECONNABORTED') {
      window.$store.dispatch('msg/showErrorMsg', '网络超时！');
    } else {
      window.$store.dispatch('msg/showErrorMsg', '网络异常！');
    }

    return Promise.reject(response); // 返回接口返回的错误信息
  }
);

/**
 * 封装fetch
 * @param methold
 * @param url
 * @param data
 * @param isFull
 * @param config 可自定义任何属性，优先级最高
 * @returns {Promise}
 */

export default function fetch({ methold = 'get', url, params = null, isFull = false, config = {}, isSSO = false }) {
  let paramsNew = null;
  if (params) {
    paramsNew = {};
    // 去除所有-1
    Object.keys(params).forEach(key => {
      if (params[key] !== -1 && params[key] !== '-1') {
        paramsNew[key] = params[key];
      }
    });
  }
  const option = {
    methold,
    url,
    params: paramsNew,
    isFull
  };

  return new Promise((resolve, reject) => {
    const { state } = window.$store;

    // refreshToken期间的所有请求放入请求池
    if (state.user.isFetch && option.url !== '/refresh_token') {
      fetchPoll.addPoll({ ...option, resolve, reject });
      return;
    }
    // 全局loading窗口
    if (config && option.params && window.$store.state.schoolYear.isShow && config.responseType === 'blob') {
      if (window.$store.state.schoolYear.year > 0) {
        option.params.year = window.$store.state.schoolYear.year;
      }
      if (window.$store.state.schoolYear.term > 0) {
        option.params.term = window.$store.state.schoolYear.term;
      }
    }
    if (option.isFull) window.$store.commit('PUSH_LOADING');
    if (option.methold === 'get' && option.params && !option.url.includes('?')) {
      option.url = `${option.url}?${qs.stringify(option.params)}`;
    }
    const token = localStorage.getItem('token');
    const clientId = localStorage.getItem('clientId');
    const headers = { Authorization: state.user.access_token || params.access_token, ...config.headers };
    if (token) {
      headers.token = token;
    }
    if (clientId) {
      headers.client_id = clientId;
    }
    axios
      .request({
        url: option.url,
        method: option.methold,
        data: { userId: state.user ? state.user.info.id : '', ...option.params },
        baseURL: isSSO ? env.ssoBaseUrl : env.baseUrl,
        ...config,
        headers
      })
      .then(response => {
        window.$store.commit('SHIFT_LOADING');
        // token过期
        if (response.status === 401) {
          fetchPoll.addPoll({ ...option, resolve, reject });
          window.$store
            .dispatch('user/setAuth')
            .then(() => {
              fetchPoll.list.map(item => {
                fetchPoll.delPoll(item);
                fetch(item)
                  .then(req => item.resolve(req))
                  .catch(e => item.reject(e));
                return [];
              });
            })
            .catch(e => {
              if (e && e.isFetch) return;
              window.$store.dispatch('msg/showErrorMsg', `授权失败: ${e.statusText}，请退出重新登录！`);
            });
        } else {
          fetchPoll.delPoll(option);
          resolve(response.data);
        }
      })
      .catch(err => {
        window.$store.commit('SHIFT_LOADING');
        fetchPoll.delPoll(option);
        reject(err);
      });
  }).catch(e => {
    if (e && e.data && e.data.type === 'application/json' && e.config.responseType === 'blob') {
      const reader = new FileReader();
      reader.onload = en => {
        try {
          const json = JSON.parse(en.target.result);
          window.$store.dispatch('msg/showErrorMsg', json.msg || '下载文件失败！');
        } catch (error) {
          window.$store.dispatch('msg/showErrorMsg', '下载文件失败！');
        }
      };
      reader.readAsText(e.data, ['utf-8']);
      return '';
    }
    if (e) {
      let msg = e.statusText || '服务异常！';
      if (e.data && e.data.msg) {
        ({ msg } = e.data);
        if (e.data.error_code === fetchCode.INVALID) {
          msg = '登录过期，请重新登录';
        }
      }
      window.$store.dispatch('msg/showErrorMsg', msg);
    }
    if (e && e.data && e.data.error_code) {
      const { error_code } = e.data;
      if (error_code === fetchCode.NEED_LOGIN || error_code === fetchCode.INVALID) {
        window.$store.dispatch('user/logout', true);
        return '';
      }
      if (fetchCode.NEED_CATCH_LIST.includes(e.data.error_code)) {
        // 异常码的在列表的才需要自行处理
        return Promise.reject(e);
      }
    }
    return '';
  });
}
